import { useCallback, useEffect, useState } from "react";
import { milestonesApi } from "../api/endpoints/milestones";

/**
 * Loads + bulk-upserts milestone selections for one child.
 * `items` is the merged catalogue (every catalogue entry is present;
 * `selection` is null when the user hasn't picked a timing yet).
 */
export function useMilestones(childId) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(Boolean(childId));
    const [error, setError] = useState(null);

    const refetch = useCallback(async () => {
        if (!childId) {
            setItems([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await milestonesApi.list(childId);
            setItems(data);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [childId]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    const bulkUpsert = useCallback(
        async (entries) => {
            const updated = await milestonesApi.bulkUpsert(childId, entries);
            setItems(updated);
            return updated;
        },
        [childId]
    );

    return { items, loading, error, refetch, bulkUpsert };
}

/**
 * Reminders → Milestones CDSS watchlist.
 *
 * Loads `GET /milestones/watchlist` — one row per (child, milestone) where
 * the child has aged past the milestone's band upper bound AND has no
 * recorded 'yes' for that milestone. Already-achieved milestones are
 * filtered server-side and never appear in the response.
 *
 * `data.items` is `MissedMilestoneRow[]` from the BE schema
 * (see phm-web-backend/app/schemas/milestone.py).
 */
export function useMilestoneWatchlist() {
    const [data, setData] = useState({ items: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await milestonesApi.getWatchlist();
            setData(result || { items: [] });
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refetch();
    }, [refetch]);

    return { data, loading, error, refetch };
}
